/**
 * client/src/lib/jobs.ts — the live job feed, and the four phases of an ingest.
 *
 * ## The stream
 *
 * `GET /api/v1/events?book=<bookId>` (§10; `server/src/routes/jobs.ts`, named events
 * `jobs | segment | narration | end`). P1 listens for `jobs` and `end`.
 *
 * ⚠️ **The stream is a delta channel, not the state.** We seed from `GET /api/v1/jobs?book=` first
 * and then attach the EventSource, so the screen is correct even if the first event we see is the
 * *second* thing that happened. A UI that renders only what it heard shows an empty ingest to
 * anyone who opened the page late — which, for a five-minute ingest, is almost everybody.
 *
 * ⚠️ **And there is a polling fallback, deliberately.** SSE is the single most fragile hop in this
 * deploy: AGENTS.md records that Caddy's `encode` buffers an event stream so it delivers *nothing*
 * until it ends, that it fails in production and nowhere else because dev talks to Vite directly,
 * and that the job stream is one of the two feeds it silently kills. The Caddyfile excludes
 * `/api/v1/events*` from compression and sets `flush_interval -1`, and both of those are one
 * config edit away from being wrong. Falling back to a 2.5 s poll turns that from "the ingest
 * screen hangs forever" into "the ingest screen is a little less smooth".
 *
 * ## The phases
 *
 * §3 runs an ingest as parse → blocks → structure → pages, and the worker reports its position with
 * `POST /internal/jobs/:id/progress { done, total, message }` (§8.2). The phase is derived from that
 * message by keyword, and an unrecognised message renders **verbatim** rather than being dropped: a
 * progress line we don't understand is still the most informative thing on the screen, and quietly
 * discarding it is how a stalled ingest looks idle.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { abApi } from "./api.ts";
import type { JobRow } from "./api.ts";

export const INGEST_PHASES = [
  {
    key: "parse",
    label: "Reading the file",
    detail: "Unpacking it and taking the reading order from the spine — never the table of contents.",
    match: /\b(probe|parse|parsing|unzip|spine|extract|epub|pdf|source)\b/i,
  },
  {
    key: "blocks",
    label: "Building the text",
    detail:
      "Paragraphs, verse lines, footnotes and images, in one walk — and sentences split once, here, and stored.",
    match: /\b(block|blocks|segment|segmenting|sentence|sentences|image|images|note|notes)\b/i,
  },
  {
    key: "structure",
    label: "Finding where the book begins",
    detail: "Eight signals, scored, with the winning reason recorded so you can disagree with it.",
    // ⚠️ No `chapter` here. "chapter" is far more likely to appear as a *counter* in another
    // phase's message ("building blocks: chapter 12 of 42") than as the subject of this one, and
    // matching it here walks the progress list forward two steps at the wrong moment.
    match: /\b(structure|structuring|heading|headings|bodystart|body start|begins|front matter|toc)\b/i,
  },
  {
    key: "pages",
    label: "Cutting pages",
    detail: "Runs of about 1,500 characters, snapped to block ends, never crossing a chapter.",
    match: /\b(page|pages|paginat\w*|cover|covers|import|importing|writing)\b/i,
  },
] as const;

export type IngestPhaseKey = (typeof INGEST_PHASES)[number]["key"];

/**
 * Which phase a progress message is describing, as an index into `INGEST_PHASES`.
 *
 * **Earliest keyword wins.** A progress line names its subject before its counters — "building
 * blocks: chapter 12 of 42" is about blocks, and its only structure-ish word is a counter three
 * words later. Last-match-wins reads that as the structure phase and jumps the list forward; taking
 * the leftmost hit reads it correctly.
 *
 * Returns −1 for a message we don't recognise, and the caller shows the message on its own — a
 * progress line we don't understand is still the most informative thing on the screen.
 */
export function phaseOf(message: string | null): number {
  if (!message) return -1;
  let best = -1;
  let bestAt = Number.POSITIVE_INFINITY;
  for (let i = 0; i < INGEST_PHASES.length; i++) {
    const hit = INGEST_PHASES[i]!.match.exec(message);
    if (hit && hit.index < bestAt) {
      best = i;
      bestAt = hit.index;
    }
  }
  return best;
}

export const isTerminal = (j: JobRow): boolean =>
  j.status === "done" || j.status === "failed" || j.status === "cancelled";

/** The ingest job for a book: the newest non-cancelled one on the `ingest` lane. */
export function ingestJobOf(jobs: readonly JobRow[]): JobRow | null {
  const candidates = jobs.filter((j) => j.lane === "ingest" && j.status !== "cancelled");
  if (candidates.length === 0) return null;
  return candidates.reduce((a, b) => (b.createdAt > a.createdAt ? b : a));
}

export interface JobStream {
  jobs: JobRow[];
  /** True while the SSE connection is open. False means we are polling instead — see the header. */
  live: boolean;
  /** Null until the first answer arrives; `[]` afterwards if there are genuinely no jobs. */
  loaded: boolean;
  error: Error | null;
  refresh: () => void;
}

/**
 * Subscribe to a book's jobs.
 *
 * `enabled: false` tears the stream down — the Book screen stops listening the moment the edition is
 * `ready`, because an EventSource left open per tab is a socket cloud-2 holds for the session and
 * there are eleven other services on that box.
 */
export function useJobStream(bookId: string | null, enabled = true): JobStream {
  const [jobs, setJobs] = useState<JobRow[]>([]);
  const [live, setLive] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [nonce, setNonce] = useState(0);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  // Held in a ref so the poll and the stream can merge into the same list without either of them
  // being a render dependency.
  const jobsRef = useRef<JobRow[]>([]);

  useEffect(() => {
    if (!bookId || !enabled) {
      setLive(false);
      return;
    }
    let alive = true;
    let source: EventSource | null = null;
    let pollTimer: number | null = null;

    /** Merge by id, newest write wins, and keep a stable order so rows don't jump under a thumb. */
    const merge = (incoming: JobRow[]) => {
      if (!alive) return;
      const byId = new Map(jobsRef.current.map((j) => [j.id, j]));
      for (const j of incoming) byId.set(j.id, j);
      const next = [...byId.values()].sort((a, b) => a.createdAt - b.createdAt);
      jobsRef.current = next;
      setJobs(next);
      setLoaded(true);
    };

    const poll = async () => {
      try {
        const res = await abApi.jobs(bookId);
        merge(res.jobs ?? []);
        if (alive) setError(null);
      } catch (err: unknown) {
        if (alive) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setLoaded(true);
        }
      }
    };

    const startPolling = () => {
      if (pollTimer !== null || !alive) return;
      pollTimer = window.setInterval(() => void poll(), 2500);
    };

    const attach = () => {
      try {
        source = new EventSource(`/api/v1/events?book=${encodeURIComponent(bookId)}`);
      } catch {
        startPolling();
        return;
      }
      source.onopen = () => {
        if (!alive) return;
        setLive(true);
        // The stream is healthy; stop the belt-and-braces poll.
        if (pollTimer !== null) {
          clearInterval(pollTimer);
          pollTimer = null;
        }
      };
      // The server names its events (`event: jobs`), but a bare `data:` frame is still legal SSE and
      // arrives as `message`. Listening to both costs one line and saves an afternoon.
      const onJobs = (e: MessageEvent<string>) => merge(parseJobs(e.data));
      source.addEventListener("jobs", onJobs as EventListener);
      source.onmessage = onJobs;
      source.addEventListener("end", () => {
        // The server is finished with this book. Take one more authoritative read so a final
        // state that raced the close is not missed, then stop.
        void poll();
        setLive(false);
        source?.close();
        source = null;
      });
      source.onerror = () => {
        if (!alive) return;
        setLive(false);
        // EventSource retries on its own, but if the proxy is buffering us we would wait forever
        // for a message that is sitting in a compressor. Poll alongside it from here on.
        startPolling();
      };
    };

    // Seed first, then attach. See the header: the stream is deltas, not state.
    void poll().then(() => {
      if (alive) attach();
    });
    // If the seed itself is slow, do not leave the screen dead in the meantime.
    const seedGuard = window.setTimeout(startPolling, 4000);

    return () => {
      alive = false;
      window.clearTimeout(seedGuard);
      if (pollTimer !== null) clearInterval(pollTimer);
      source?.close();
    };
  }, [bookId, enabled, nonce]);

  return { jobs, live, loaded, error, refresh };
}

/**
 * An event frame carries either a single job or a list of them. Anything else is discarded rather
 * than thrown — one malformed frame must not take down a screen watching a five-minute ingest.
 */
function parseJobs(data: string): JobRow[] {
  let parsed: unknown;
  try {
    parsed = JSON.parse(data) as unknown;
  } catch {
    return [];
  }
  if (Array.isArray(parsed)) return parsed.filter(isJobRow);
  if (parsed && typeof parsed === "object") {
    const o = parsed as Record<string, unknown>;
    if (Array.isArray(o.jobs)) return o.jobs.filter(isJobRow);
    if (isJobRow(o.job)) return [o.job];
    if (isJobRow(o)) return [o];
  }
  return [];
}

function isJobRow(v: unknown): v is JobRow {
  return (
    !!v &&
    typeof v === "object" &&
    typeof (v as JobRow).id === "string" &&
    typeof (v as JobRow).status === "string"
  );
}
